import React, { useEffect, useRef, useState } from 'react';
import { Box, BoxProps, usePrefersReducedMotion } from '@chakra-ui/react';

// Fades a piece of text out, swaps it while invisible, then fades the new text in.
// Used for single-line headings (e.g. the charger status / fault title) where the
// content can change length: swapping at opacity 0 hides any height change, so there's
// no overlap and no visible layout jump, unlike an overlapping crossfade.
const FADE_MS = 160;

type FadingTextProps = Omit<BoxProps, 'children'> & {
  // Identity of the current text. When it changes, the fade-out → swap → fade-in runs.
  // Re-renders that keep the same key (e.g. unrelated state changes) do not animate.
  textKey: string | number;
  children: React.ReactNode;
};

export const FadingText = ({ textKey, children, ...rest }: FadingTextProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  // The text currently painted (committed only at opacity 0, mid-fade).
  const [shown, setShown] = useState<{ key: string | number; node: React.ReactNode }>({
    key: textKey,
    node: children,
  });
  const [opacity, setOpacity] = useState(1);

  // The key we're currently fading toward, so frequent re-renders during a fade don't
  // restart it.
  const pendingKeyRef = useRef(textKey);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (prefersReducedMotion) {
      // Cancel any in-flight fade so a stale timeout can't commit old content after
      // reduced-motion has been enabled (e.g. OS setting flips while the app is open).
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pendingKeyRef.current = textKey;
      setOpacity(1);
      if (shown.key !== textKey || shown.node !== children) setShown({ key: textKey, node: children });
      return;
    }

    if (textKey === shown.key) {
      // Same text — keep the rendered node in sync (e.g. a re-translation) without fading.
      pendingKeyRef.current = textKey;
      if (shown.node !== children) setShown({ key: textKey, node: children });
      return;
    }

    // Already fading toward this key — don't restart the fade on incidental re-renders.
    if (textKey === pendingKeyRef.current) return;
    pendingKeyRef.current = textKey;

    // Fade the old text out, then swap + fade the new text in while invisible.
    setOpacity(0);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShown({ key: textKey, node: children });
      setOpacity(1);
    }, FADE_MS);
  }, [textKey, children, prefersReducedMotion, shown.key, shown.node]);

  return (
    <Box
      data-testid="fading-text"
      style={prefersReducedMotion ? undefined : { opacity, transition: `opacity ${FADE_MS}ms ease-in-out` }}
      {...rest}
    >
      {shown.node}
    </Box>
  );
};
